// Import Vue to access the global Vue.axios instance
import Vue from 'vue';
import { API_ENDPOINTS, EMAIL_NOTIFICATION, CRON, NOTIFICATION_SCHEDULE } from '../utils/constants';
import { getApiErrorMessage, parseCycleSchedulerResponse } from '../utils/apiErrorUtils';
import { buildSchedulePayload } from '../utils/notificationScheduleUtils';

function hourToStartTime(time) {
  if (time === null || time === undefined || time === '') return NOTIFICATION_SCHEDULE.DEFAULT_START_TIME;
  const hour = String(time) === '24' ? '00' : String(time).padStart(2, '0');
  return `${hour}:00`;
}

function getAxios() {
  return Vue.axios;
}

export default {
  namespaced: true,
  
  state: {
    loading: false,
    initialLoading: false, // For skeleton loader during initial data fetch
    error: null,
    emailNotifications: [],
    emailNotificationsLoading: false,
    isCycleInActive: false,
    dashboardData: null,
    defaultDashboardData: null,
    usersFormData: null,
    countryWiseLocations: null,
    clientData: null,
    cycleDetails: null, // Store cycle details
    activeLocations: [], // Locations with status true
    disabledLocations: [], // Locations with status false
    activeCountries: [], // Countries with at least one active location
    disabledCountries: [] // Countries with all locations disabled
  },
  
  mutations: {
    SET_EMAIL_NOTIFICATIONS_LOADING(state, isLoading) {
      state.emailNotificationsLoading = isLoading;
    },
    SET_LOADING(state, isLoading) {
      state.loading = isLoading;
    },
    
    SET_INITIAL_LOADING(state, isLoading) {
      state.initialLoading = isLoading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    SET_EMAIL_NOTIFICATIONS(state, notifications) {
      state.emailNotifications = notifications || [];
    },
    
    SET_CYCLE_STATUS(state, isInactive) {
      state.isCycleInActive = isInactive;
    },
    
    SET_DASHBOARD_DATA(state, data) {
      state.dashboardData = data;
    },
    
    SET_DEFAULT_DASHBOARD_DATA(state, data) {
      state.defaultDashboardData = data;
    },
    
    SET_USERS_FORMDATA(state, data) {
      state.usersFormData = data;
    },
    
    SET_COUNTRYWISE_LOCATIONS(state, data) {
      state.countryWiseLocations = data;
      
      // Process location status
      if (data && Array.isArray(data)) {
        const activeLocations = [];
        const disabledLocations = [];
        const activeCountries = [];
        const disabledCountries = [];
        
        data.forEach(countryData => {
          const allDisabled = countryData.locations?.every(loc => loc.status === false);
          
          if (allDisabled) {
            disabledCountries.push(countryData.country);
          } else {
            activeCountries.push(countryData.country);
          }
          
          countryData.locations?.forEach(loc => {
            if (loc.status !== false) {
              activeLocations.push(loc);
            } else {
              disabledLocations.push(loc);
            }
          });
        });
        
        state.activeLocations = activeLocations;
        state.disabledLocations = disabledLocations;
        state.activeCountries = activeCountries;
        state.disabledCountries = disabledCountries;
      }
    },
    
    SET_CLIENT_DATA(state, data) {
      state.clientData = data;
    },
    
    SET_CYCLE_DETAILS(state, data) {
      state.cycleDetails = data;
    },
    
    RESET_DASHBOARD_STATE(state) {
      state.dashboardData = null;
      state.defaultDashboardData = null;
      state.countryWiseLocations = null;
      state.usersFormData = null;
      state.activeLocations = [];
      state.disabledLocations = [];
      state.activeCountries = [];
      state.disabledCountries = [];
    }
  },
  
  actions: {
    /**
     * Initialize dashboard with all required data
     * This action fetches all dashboard data in parallel and properly handles loading states
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving when all data is loaded
     */
    async initializeDashboard({ commit, dispatch }, { cycleId }) {
      commit('SET_INITIAL_LOADING', true);
      commit('RESET_DASHBOARD_STATE');
      commit('SET_ERROR', null);
      
      try {
        // Fetch all data in parallel for better performance
        const [dashboardResult, defaultDashboardResult, usersFormResult, locationsResult] = await Promise.all([
          dispatch('getDashboard', { cycleId }),
          dispatch('getDefaultDashboard', { cycleId }),
          dispatch('getUsersFormData'),
          dispatch('getCountryWiseLocations', { cycleId })
        ]);
        
        return {
          success: true,
          dashboard: dashboardResult,
          defaultDashboard: defaultDashboardResult,
          usersForm: usersFormResult,
          locations: locationsResult
        };
      } catch (err) {
        console.error('[Dashboard Store]: initializeDashboard error', err);
        commit('SET_ERROR', err.message || 'Failed to initialize dashboard');
        commit('sbError', err.message || 'Failed to load dashboard data', { root: true });
        return { success: false, message: err.message };
      } finally {
        commit('SET_INITIAL_LOADING', false);
      }
    },
    
    /**
     * Get cycle details
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving to cycle details
     */
    async getCycleDetails({ commit }, { cycleId }) {
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.GET_DASHBOARD_DETAILS}${cycleId}`);
        
        if (response?.data?.status === 'success') {
          const data = response.data.data[0] || {};
          commit('SET_CYCLE_DETAILS', data);
          return { success: true, data };
        } else {
          const errorMsg = response.data?.message || 'Error retrieving cycle details';
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error('[Dashboard Store]: getCycleDetails error', err);
        const errorMsg = err.message || 'Unknown error fetching cycle details';
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
    },

    /**
     * Get all email notifications for a project
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving to the email notifications
     */
    async getAllEmailNotifications({ commit }, { cycleId, notificationType = EMAIL_NOTIFICATION.FEATURE_TYPES.DASHBOARD }) {
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.GET_ALL_SCHEDULER}/${cycleId}`,
          { notificationType }
        );
        if (response?.data?.status === 'success') {
          const parsed = parseCycleSchedulerResponse(response.data);
          commit('SET_CYCLE_STATUS', parsed.isCycleActive === false);
          commit('SET_EMAIL_NOTIFICATIONS', parsed.notifications);
          return parsed.notifications;
        }
        const errorMsg = response.data?.message || 'Error retrieving notifications';
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return [];
      } catch (err) {
        console.error('[dashboard]: getAllEmailNotifications', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error fetching notifications');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return [];
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    
    /**
     * Add a new email notification
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains notification details
     * @returns {Promise} - Promise resolving to the API response
     */
    async addEmailNotification({ commit, state }, { cycleId, notificationData }) {
      // Check if maximum limit reached
      if (state.emailNotifications.length >= EMAIL_NOTIFICATION.MAX_EMAIL_INSTANCES) {
        const errorMsg = `The current cycle has reached the maximum limit of ${EMAIL_NOTIFICATION.MAX_EMAIL_INSTANCES} notifications. You cannot add more.`;
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      }
      
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      commit('SET_ERROR', null);
      const startTime = notificationData.startTime
        || notificationData.schedule?.startTime
        || hourToStartTime(notificationData.time);
      const schedule = { ...buildSchedulePayload({ startTime }), frequencyHours: 24 };
      const data = {
        feature: EMAIL_NOTIFICATION.FEATURE_TYPES.DASHBOARD,
        flag: notificationData.flag !== false,
        type: EMAIL_NOTIFICATION.TYPE,
        name: notificationData.name,
        subject: notificationData.subject,
        mailBody: notificationData.mailBody,
        cycle_id: cycleId.toString(),
        schedulerTiming: CRON.hourToCronFormat(notificationData.time),
        meta: {
          location_key: notificationData.locations || [],
          toUsers: notificationData.emails || [],
          startDate: notificationData.startDate,
          endDate: notificationData.endDate,
          schedule,
        }
      };
      try {
        const response = await getAxios().post(API_ENDPOINTS.UPDATE_CREATE_SCHEDULER, {
          featureDetails: data
        });
        if (response?.data?.status === 'success') {
          commit('sbSuccess', response.data.message || 'Email notification created', { root: true });
          return { success: true, data: response.data };
        }
        const errorMsg = response.data?.message || 'Error creating notification';
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } catch (err) {
        console.error('[dashboard]: addEmailNotification', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error creating notification');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    
    /**
     * Update an existing email notification
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains notification details with _id
     * @returns {Promise} - Promise resolving to the API response
     */
    async updateEmailNotification({ commit }, { cycleId, notificationData }) {
      if (!notificationData._id) {
        const errorMsg = "Notification ID is required for update";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      }
      
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      commit('SET_ERROR', null);
      const startTime = notificationData.startTime
        || notificationData.schedule?.startTime
        || hourToStartTime(notificationData.time);
      const schedule = { ...buildSchedulePayload({ startTime }), frequencyHours: 24 };
      const data = {
        feature: EMAIL_NOTIFICATION.FEATURE_TYPES.DASHBOARD,
        flag: notificationData.flag !== false,
        type: EMAIL_NOTIFICATION.TYPE,
        name: notificationData.name,
        subject: notificationData.subject,
        mailBody: notificationData.mailBody,
        cycle_id: cycleId.toString(),
        schedulerTiming: CRON.hourToCronFormat(notificationData.time),
        _id: notificationData._id,
        meta: {
          location_key: notificationData.locations || [],
          toUsers: notificationData.emails || [],
          startDate: notificationData.startDate,
          endDate: notificationData.endDate,
          schedule,
        }
      };
      try {
        const response = await getAxios().post(API_ENDPOINTS.UPDATE_CREATE_SCHEDULER, {
          featureDetails: data
        });
        if (response?.data?.status === 'success') {
          commit('sbSuccess', response.data.message || 'Email notification updated', { root: true });
          return { success: true, data: response.data };
        }
        const errorMsg = response.data?.message || 'Error updating notification';
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } catch (err) {
        console.error('[dashboard]: updateEmailNotification', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error updating notification');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    
    /**
     * Enable or disable an email notification
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains notification item and enable/disable action
     * @returns {Promise} - Promise resolving to the API response
     */
    async toggleEmailNotificationStatus({ commit }, { notification, enable }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      const data = {
        feature: notification.feature,
        flag: enable,
        type: notification.type,
        _id: notification._id,
        subject: notification.subject,
        mailBody: notification.mailBody,
        cycle_id: notification.cycle_id,
        schedulerTiming: notification.schedulerTiming,
        meta: {
          location_key: notification.meta.location_key,
          toUsers: notification.meta.toUsers,
          startDate: notification.meta.startDate,
          endDate: notification.meta.endDate,
        }
      };
      
      try {
        const response = await getAxios().post(API_ENDPOINTS.UPDATE_CREATE_SCHEDULER, {
          featureDetails: data
        });
        
        if (response?.data?.status === "success") {
          const successMsg = enable ? "Notification Enabled" : "Notification Disabled";
          commit('sbSuccess', successMsg, { root: true });
          return { success: true, data: response.data };
        } else {
          const errorMsg = response.data?.message || "Error updating notification status";
          commit('SET_ERROR', errorMsg);
          commit('sbError', errorMsg, { root: true });
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error('[dashboard]: toggleEmailNotificationStatus', err);
        const errorMsg = getApiErrorMessage(err, 'Unknown error updating notification status');
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteEmailNotification({ commit, state }, { cycleId, id }) {
      commit('SET_EMAIL_NOTIFICATIONS_LOADING', true);
      try {
        await getAxios().post(`${API_ENDPOINTS.DELETE_CYCLE_EMAIL_NOTIFICATION}/${cycleId}/${id}`, {});
        commit('SET_EMAIL_NOTIFICATIONS', state.emailNotifications.filter(
          (item) => String(item._id) !== String(id)
        ));
        commit('sbSuccess', EMAIL_NOTIFICATION.MESSAGES.SUCCESS_DELETE, { root: true });
      } catch (err) {
        console.error('[dashboard]: deleteEmailNotification', err);
        const errorMsg = getApiErrorMessage(err, EMAIL_NOTIFICATION.MESSAGES.FAILED_DELETE);
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        throw err;
      } finally {
        commit('SET_EMAIL_NOTIFICATIONS_LOADING', false);
      }
    },
    
    /**
     * Get dashboard details for a cycle
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving to dashboard details
     */
    async getDashboardDetails({ commit }, { cycleId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.GET_DASHBOARD_DETAILS}${cycleId}`);
        
        if (response?.data?.status === "success") {
          return { success: true, data: response.data.data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving dashboard details";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching dashboard details";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get dashboard data for a cycle
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId and optional locationId
     * @returns {Promise} - Promise resolving to dashboard data
     */
    async getDashboard({ commit }, { cycleId, locationId = null }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const url = locationId 
          ? `${API_ENDPOINTS.GET_DASHBOARD}${cycleId}?location=${locationId}`
          : `${API_ENDPOINTS.GET_DASHBOARD}${cycleId}`;
        
        const response = await getAxios().post(url);
        
        if (response?.data?.status === "success" && response.data.data.length > 0) {
          commit('SET_DASHBOARD_DATA', response.data.data[0] || {});
          return { success: true, data: response.data.data[0] };
        } else {
          const errorMsg = response.data?.message || "Error retrieving dashboard";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching dashboard";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get default dashboard data for a cycle
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving to default dashboard data
     */
    async getDefaultDashboard({ commit }, { cycleId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.GET_DEFAULT_DASHBOARD}${cycleId}`);
        
        if (response?.data?.status === "success") {
          commit('SET_DEFAULT_DASHBOARD_DATA', response.data.data);
          return { success: true, data: response.data.data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving default dashboard";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg, data: [] };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching default dashboard";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg, data: [] };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get users form data
     * @param {Object} context - Vuex context
     * @returns {Promise} - Promise resolving to users form data
     */
    async getUsersFormData({ commit }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(API_ENDPOINTS.GET_USERS_FORMDATA);
        
        if (response?.data?.status === "success") {
          commit('SET_USERS_FORMDATA', response.data.data[0]);
          return { success: true, data: response.data.data[0] };
        } else {
          const errorMsg = response.data?.message || "Error retrieving users form data";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching users form data";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get dashboard data with filters
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId and filter data
     * @returns {Promise} - Promise resolving to filtered dashboard data
     */
    async getDashboardWithFilter({ commit }, { cycleId, filterData }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(
          `${API_ENDPOINTS.GET_DASHBOARD_WITH_FILTERS}${cycleId}`,
          { data: filterData }
        );
        
        if (response?.data?.status === "success") {
          return { success: true, data: response.data.data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving filtered dashboard";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg, data: [] };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching filtered dashboard";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg, data: [] };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get country-wise locations for a cycle
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains cycleId
     * @returns {Promise} - Promise resolving to location data
     */
    async getCountryWiseLocations({ commit }, { cycleId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.GET_COUNTRYWISE_LOCATION}${cycleId}`);
        
        if (response?.data?.status === "success") {
          commit('SET_COUNTRYWISE_LOCATIONS', response.data.data);
          return { success: true, data: response.data.data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving location data";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg, data: [] };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching location data";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg, data: [] };
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Get client notification settings
     * @param {Object} context - Vuex context
     * @param {Object} payload - Contains clientId
     * @returns {Promise} - Promise resolving to client data
     */
    async getClientNotification({ commit }, { clientId }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await getAxios().post(`${API_ENDPOINTS.GET_CLIENT}${clientId}`);
        
        if (response?.data?.status === "success") {
          commit('SET_CLIENT_DATA', response.data.data[0]);
          return { 
            success: true, 
            data: response.data.data[0],
            schedulerEnabled: response.data.data[0]?.schedulerEnabled 
          };
        } else {
          const errorMsg = response.data?.message || "Error retrieving client data";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error(err);
        const errorMsg = err.message || "Unknown error fetching client data";
        commit('SET_ERROR', errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ===========================
    // V3 Dashboard Actions
    // ===========================

    /**
     * V3: Get consolidated dashboard data (replaces getCycleDetails + getDashboard + getDefaultDashboard + getDashboardWithFilter)
     * Single API call returns metadata, GL-type summary, and category breakdown.
     * Supports optional filters for location and user.
     *
     * @param {Object} context - Vuex context
     * @param {Object} payload - { cycleId, filters?: { location: [...], user: [...] } }
     * @returns {Promise} - { success, data: { metadata, summary, categoryBreakdown } }
     */
    async getDashboardV3({ commit }, { cycleId, filters = null }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const body = {};
        if (filters && (filters.location?.length || filters.user?.length)) {
          body.filters = filters;
        }

        const response = await getAxios().post(`${API_ENDPOINTS.V3_GET_DASHBOARD}${cycleId}`, body);

        if (response?.data?.status === "success") {
          const data = response.data.data;
          commit('SET_DASHBOARD_DATA', data);
          commit('SET_DEFAULT_DASHBOARD_DATA', data.categoryBreakdown);
          return { success: true, data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving dashboard";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error('[Dashboard Store]: getDashboardV3 error', err);
        const errorMsg = err.message || "Unknown error fetching dashboard";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * V3: Get consolidated form data (replaces getUsersFormData + getCountryWiseLocations + getClientNotification)
     * Uses the shared /v3/getformdata endpoint (same as tasklist page).
     *
     * @param {Object} context - Vuex context
     * @returns {Promise} - { success, data: { users, locations, schedulerEnabled, ... } }
     */
    async getFormDataV3({ commit, rootState }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const clientId = rootState.clientdetails?._id || rootState.clientId;
        const response = await getAxios().post(API_ENDPOINTS.V3_GET_FORMDATA, { client: clientId });

        if (response?.data?.status === "success") {
          const data = response.data.data;

          // Populate users form data (backward compat)
          commit('SET_USERS_FORMDATA', { users: data.users || [], infoowners: [] });

          // Build country-wise locations from flat locations catalog (name/country display).
          // Location pickers use fetchActorLocations / actorLocations separately.
          const locationsByCountry = {};
          for (const loc of (data.locations || [])) {
            const country = loc.country || 'Unknown';
            if (!locationsByCountry[country]) {
              locationsByCountry[country] = { country, locations: [] };
            }
            locationsByCountry[country].locations.push({
              id: loc._id,
              name: loc.name,
              status: true,
            });
          }
          commit('SET_COUNTRYWISE_LOCATIONS', Object.values(locationsByCountry));

          // Populate client data (schedulerEnabled)
          commit('SET_CLIENT_DATA', { schedulerEnabled: data.schedulerEnabled || false });

          return { success: true, data };
        } else {
          const errorMsg = response.data?.message || "Error retrieving form data";
          commit('SET_ERROR', errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err) {
        console.error('[Dashboard Store]: getFormDataV3 error', err);
        const errorMsg = err.message || "Unknown error fetching form data";
        commit('SET_ERROR', errorMsg);
        commit('sbError', errorMsg, { root: true });
        return { success: false, message: errorMsg };
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * V3: Initialize dashboard with all required data using v3 endpoints.
     * Replaces initializeDashboard — calls getFormDataV3 + getDashboardV3 in parallel.
     *
     * @param {Object} context - Vuex context
     * @param {Object} payload - { cycleId, filters?: { location: [...], user: [...] } }
     * @returns {Promise}
     */
    async initializeDashboardV3({ commit, dispatch }, { cycleId, filters = null }) {
      commit('SET_INITIAL_LOADING', true);
      commit('RESET_DASHBOARD_STATE');
      commit('SET_ERROR', null);

      try {
        const [formResult, dashboardResult] = await Promise.all([
          dispatch('getFormDataV3'),
          dispatch('getDashboardV3', { cycleId, filters }),
        ]);

        return {
          success: formResult.success && dashboardResult.success,
          formData: formResult,
          dashboard: dashboardResult,
        };
      } catch (err) {
        console.error('[Dashboard Store]: initializeDashboardV3 error', err);
        commit('SET_ERROR', err.message || 'Failed to initialize dashboard');
        commit('sbError', err.message || 'Failed to load dashboard data', { root: true });
        return { success: false, message: err.message };
      } finally {
        commit('SET_INITIAL_LOADING', false);
      }
    },
  },
  
  getters: {
    isLoading: state => state.loading,
    isInitialLoading: state => state.initialLoading,
    getError: state => state.error,
    getEmailNotifications: state => state.emailNotifications,
    isCycleInActive: state => state.isCycleInActive,
    
    // Get total number of notifications
    getNotificationCount: state => state.emailNotifications.length,
    
    // Check if maximum notifications limit is reached
    isMaxNotificationsReached: state => state.emailNotifications.length >= EMAIL_NOTIFICATION.MAX_EMAIL_INSTANCES,
    
    // Dashboard data getters
    getDashboardData: state => state.dashboardData,
    getDefaultDashboardData: state => state.defaultDashboardData,
    getUsersFormData: state => state.usersFormData,
    getCountryWiseLocations: state => state.countryWiseLocations,
    getClientData: state => state.clientData,
    isClientSchedulerEnabled: state => state.clientData?.schedulerEnabled || false,
    getCycleDetails: state => state.cycleDetails,
    
    // Location status getters
    getActiveLocations: state => state.activeLocations,
    getDisabledLocations: state => state.disabledLocations,
    getActiveCountries: state => state.activeCountries,
    getDisabledCountries: state => state.disabledCountries,
    
    // Check if a location is disabled
    isLocationDisabled: state => locationId => {
      return state.disabledLocations.some(loc => loc.id === locationId);
    },
    
    // Check if a country is disabled (all locations disabled)
    isCountryDisabled: state => countryName => {
      return state.disabledCountries.includes(countryName);
    },
    
    // Get filtered locations (only active ones)
    getFilteredLocations: state => {
      return state.activeLocations || [];
    },
    
    // Get filtered countries (only those with at least one active location)
    getFilteredCountries: state => {
      if (!state.countryWiseLocations) return [];
      return state.countryWiseLocations.filter(country => 
        !state.disabledCountries.includes(country.country)
      );
    }
  }
};
